import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Paper, Tooltip, IconButton, CircularProgress } from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const CONTRACT_ADDRESS = '0x6488861b401F427D13B6619C77C297366bCf6386';
const POOL_ADDRESS = '0x1874FaaF626923329340b165D50CA3be5448EEeA';
const TOTAL_SUPPLY = 1_000_000;

const ALLOCATIONS = [
  { label: 'Treasury', pct: 96.88, color: '#a78bfa', desc: 'Protocol-controlled reserve for future development (0xbfae…688)' },
  { label: 'Liquidity Pool', pct: 1.91, color: '#22c55e', desc: 'Uniswap V3 / SushiSwap E3D/WETH pool addresses' },
  { label: 'Public Circulating', pct: 1.16, color: '#38bdf8', desc: 'Tokens in public hands — traders, holders, DEX buyers' },
  { label: 'Staking / NFT Contract', pct: 0.05, color: '#f59e0b', desc: 'NFT manager and staking contract reserves (0xeED4…D47)' },
];

const VESTING = [
  { bucket: 'Treasury', cliff: '—', duration: '—', notes: 'Unlocked — governed by protocol multisig' },
  { bucket: 'Liquidity Pool', cliff: '—', duration: '—', notes: 'Unlocked — active in Uniswap V3 / SushiSwap' },
  { bucket: 'Staking / NFT Contract', cliff: '—', duration: '—', notes: 'Unlocked — held in NFT manager contract' },
];

const API_BASE = 'https://e3d.ai/api';
const UNISWAP_URL = `https://app.uniswap.org/explore/tokens/ethereum/${CONTRACT_ADDRESS.toLowerCase()}`;
const SUSHI_URL = `https://www.sushi.com/ethereum/pool/v3/${POOL_ADDRESS.toLowerCase()}/1624`;
const ETHERSCAN_URL = `https://etherscan.io/token/${CONTRACT_ADDRESS}`;

async function fetchOnChainMetrics() {
  const rpc = 'https://ethereum.publicnode.com';
  const call = (to, data, id) => ({ jsonrpc: '2.0', method: 'eth_call', params: [{ to, data }, 'latest'], id });
  const padAddr = (addr) => '0x70a08231' + addr.replace('0x', '').toLowerCase().padStart(64, '0');

  const [rpcRes, circRes] = await Promise.all([
    fetch(rpc, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([
        call('0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419', '0xfeaf968c', 1), // ETH/USD Chainlink
        call(POOL_ADDRESS, '0x3850c7bd', 2),                                   // slot0 (sqrtPriceX96)
        call(CONTRACT_ADDRESS.toLowerCase(), padAddr(POOL_ADDRESS), 3),         // E3D balance in pool
        call('0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2', padAddr(POOL_ADDRESS), 4), // WETH balance in pool
      ]),
    }),
    fetch(`${API_BASE}/circulating-supply`).then(r => r.text()).catch(() => null),
  ]);

  const data = await rpcRes.json();
  const byId = {};
  data.forEach((r) => { byId[r.id] = r.result; });

  const ethUsd = parseInt(byId[1].slice(0, 2 + 64 * 2).slice(66), 16) / 1e8;

  // slot0 ABI-encodes each return value as a 32-byte (64-hex-char) word.
  // Word 0 (chars 0-63): sqrtPriceX96 (uint160)
  // Word 1 (chars 64-127): tick (int24, sign-extended to 32 bytes, value in last 6 chars)
  // Pool is E3D(token0)/WETH(token1): 1.0001^tick = WETH per E3D.
  const slot0Bytes = byId[2].slice(2); // strip 0x
  const tickHex = slot0Bytes.slice(122, 128); // last 6 chars of second 32-byte word
  const tickRaw = parseInt(tickHex, 16);
  const tick = tickRaw >= 0x800000 ? tickRaw - 0x1000000 : tickRaw;
  const wethPerE3d = Math.pow(1.0001, tick);
  const usdPerE3d = wethPerE3d * ethUsd;

  const e3dInPool = parseInt(byId[3], 16) / 1e18;
  const wethInPool = parseInt(byId[4], 16) / 1e18;
  const tvl = e3dInPool * usdPerE3d + wethInPool * ethUsd;
  const fdv = TOTAL_SUPPLY * usdPerE3d;

  const circulatingSupply = circRes ? parseFloat(circRes) : null;

  return { ethUsd, usdPerE3d, wethPerE3d, e3dInPool, wethInPool, tvl, fdv, circulatingSupply };
}

function fmt(n, decimals = 2) {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
function fmtUsd(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 60px',
    maxWidth: 900,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: { padding: '14px 10px 40px' },
  },
  title: {
    fontSize: '1.9rem',
    fontWeight: 900,
    letterSpacing: -0.5,
    color: '#e5e7ff',
    textShadow: '0 0 24px rgba(77,121,255,0.5)',
    textAlign: 'left',
  },
  subtitle: {
    fontSize: 14,
    color: '#9ea4d8',
    marginTop: 6,
    textAlign: 'left',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 14,
    marginTop: 32,
    textAlign: 'left',
  },
  panel: {
    background: 'rgba(15,18,40,0.85)',
    border: '1px solid rgba(77,121,255,0.2)',
    borderRadius: 14,
    padding: '20px 22px',
    marginBottom: 16,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr 1fr' },
  },
  statCard: {
    background: 'rgba(15,18,40,0.85)',
    border: '1px solid rgba(77,121,255,0.2)',
    borderRadius: 14,
    padding: '16px 18px',
    textAlign: 'left',
  },
  statValue: {
    fontSize: '1.3rem',
    fontWeight: 900,
    color: '#4d79ff',
    lineHeight: 1.2,
    letterSpacing: -0.5,
    wordBreak: 'break-all',
  },
  statLabel: {
    fontSize: 11,
    color: '#9ea4d8',
    marginTop: 6,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  contractBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(77,121,255,0.07)',
    border: '1px solid rgba(77,121,255,0.25)',
    borderRadius: 10,
    padding: '10px 14px',
    flexWrap: 'wrap',
  },
  contractLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    flexShrink: 0,
  },
  contractAddress: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#a5b4fc',
    flex: 1,
    wordBreak: 'break-all',
    [theme.breakpoints.down('sm')]: { fontSize: 11 },
  },
  iconBtn: {
    color: '#9ea4d8',
    padding: 4,
    '&:hover': { color: '#4d79ff' },
  },
  allocationRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '11px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    gap: 12,
    '&:last-child': { borderBottom: 'none' },
  },
  allocationDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  allocationLabel: {
    fontSize: 14,
    color: '#e5e7ff',
    fontWeight: 600,
    textAlign: 'left',
  },
  allocationDesc: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: 400,
    textAlign: 'left',
  },
  allocationPct: {
    fontSize: 15,
    fontWeight: 900,
    minWidth: 48,
    textAlign: 'right',
  },
  barTrack: {
    flex: 1,
    height: 6,
    background: 'rgba(255,255,255,0.07)',
    borderRadius: 4,
    overflow: 'hidden',
    maxWidth: 120,
  },
  vestingTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    textAlign: 'left',
    paddingBottom: 10,
    paddingRight: 16,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  tdLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#e5e7ff',
    padding: '10px 16px 10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  td: {
    fontSize: 13,
    color: '#d1d5db',
    padding: '10px 16px 10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    verticalAlign: 'top',
    textAlign: 'left',
  },
  placeholder: {
    color: '#4b5563',
    fontStyle: 'italic',
  },
  tradingRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    gap: 12,
    '&:last-child': { borderBottom: 'none' },
  },
  tradingDex: {
    fontSize: 14,
    fontWeight: 700,
    color: '#e5e7ff',
    minWidth: 110,
    textAlign: 'left',
  },
  tradingPair: {
    fontSize: 13,
    color: '#9ea4d8',
    flex: 1,
    textAlign: 'left',
  },
  note: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 20,
    lineHeight: 1.6,
    textAlign: 'left',
  },
}));

export default function TokenomicsPage() {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOnChainMetrics()
      .then(setMetrics)
      .catch(() => setMetrics(null))
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(CONTRACT_ADDRESS).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box style={{ marginBottom: 32 }}>
        <Typography className={classes.title}>E3D Tokenomics</Typography>
        <Typography className={classes.subtitle}>
          Token distribution, allocations, and supply metrics for the E3D protocol
        </Typography>
      </Box>

      {/* Live supply / price stats */}
      <Typography className={classes.sectionLabel}>Supply & Price</Typography>
      <Box className={classes.statsGrid}>
        <Paper className={classes.statCard} elevation={0}>
          <Typography className={classes.statValue}>1,000,000</Typography>
          <Typography className={classes.statLabel}>Total Supply</Typography>
        </Paper>
        <Paper className={classes.statCard} elevation={0}>
          {loading ? (
            <CircularProgress size={22} style={{ color: '#4d79ff', margin: '4px 0', display: 'block' }} />
          ) : (
            <Typography className={classes.statValue} style={{ color: '#22c55e' }}>
              {metrics && metrics.circulatingSupply != null ? fmt(metrics.circulatingSupply, 0) : '—'}
            </Typography>
          )}
          <Typography className={classes.statLabel}>Circulating Supply</Typography>
        </Paper>
        <Paper className={classes.statCard} elevation={0}>
          {loading ? (
            <CircularProgress size={22} style={{ color: '#4d79ff', margin: '4px 0', display: 'block' }} />
          ) : (
            <Typography className={classes.statValue} style={{ fontSize: '1.1rem' }}>
              {metrics ? `$${metrics.usdPerE3d.toFixed(4)}` : '—'}
            </Typography>
          )}
          <Typography className={classes.statLabel}>Price (USD)</Typography>
        </Paper>
        <Paper className={classes.statCard} elevation={0}>
          {loading ? (
            <CircularProgress size={22} style={{ color: '#4d79ff', margin: '4px 0', display: 'block' }} />
          ) : (
            <Typography className={classes.statValue} style={{ fontSize: '1.1rem' }}>
              {metrics ? fmtUsd(metrics.fdv) : '—'}
            </Typography>
          )}
          <Typography className={classes.statLabel}>FDV</Typography>
        </Paper>
      </Box>

      {/* Contract address */}
      <Typography className={classes.sectionLabel}>Contract</Typography>
      <Paper className={classes.panel} style={{ padding: '14px 18px' }} elevation={0}>
        <Box className={classes.contractBox}>
          <Typography className={classes.contractLabel}>Ethereum</Typography>
          <Typography className={classes.contractAddress}>{CONTRACT_ADDRESS}</Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy address'}>
            <IconButton className={classes.iconBtn} size="small" onClick={handleCopy}>
              <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View on Etherscan">
            <IconButton
              className={classes.iconBtn}
              size="small"
              component="a"
              href={ETHERSCAN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Allocation breakdown */}
      <Typography className={classes.sectionLabel}>Allocation</Typography>
      <Paper className={classes.panel} elevation={0}>
        {ALLOCATIONS.map((a) => (
          <Box key={a.label} className={classes.allocationRow}>
            <Box className={classes.allocationDot} style={{ background: a.color }} />
            <Box flex={1}>
              <Typography className={classes.allocationLabel}>{a.label}</Typography>
              <Typography className={classes.allocationDesc}>{a.desc}</Typography>
            </Box>
            <Box className={classes.barTrack}>
              {a.pct !== null && (
                <Box style={{ width: `${a.pct}%`, height: '100%', background: a.color, borderRadius: 4 }} />
              )}
            </Box>
            <Typography
              className={classes.allocationPct}
              style={{ color: a.pct !== null ? a.color : '#4b5563' }}
            >
              {a.pct !== null ? `${a.pct}%` : '—'}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* Vesting schedules */}
      <Typography className={classes.sectionLabel}>Vesting Schedules</Typography>
      <Paper className={classes.panel} elevation={0}>
        <table className={classes.vestingTable}>
          <thead>
            <tr>
              <th className={classes.th}>Bucket</th>
              <th className={classes.th}>Cliff</th>
              <th className={classes.th}>Duration</th>
              <th className={classes.th}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {VESTING.map((v) => (
              <tr key={v.bucket}>
                <td className={classes.tdLabel}>{v.bucket}</td>
                <td className={classes.td}><span className={classes.placeholder}>{v.cliff}</span></td>
                <td className={classes.td}><span className={classes.placeholder}>{v.duration}</span></td>
                <td className={classes.td} style={{ color: '#9ca3af' }}>{v.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>

      {/* Trading / Liquidity */}
      <Typography className={classes.sectionLabel}>Markets</Typography>
      <Paper className={classes.panel} elevation={0}>
        <Box className={classes.tradingRow}>
          <Typography className={classes.tradingDex}>Uniswap V3</Typography>
          <Typography className={classes.tradingPair}>E3D / WETH · 0.3% fee</Typography>
          {metrics && (
            <Typography style={{ fontSize: 13, color: '#9ca3af' }}>
              TVL {fmtUsd(metrics.tvl)}
            </Typography>
          )}
          <Tooltip title="Trade on Uniswap">
            <IconButton
              className={classes.iconBtn}
              size="small"
              component="a"
              href={UNISWAP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon style={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box className={classes.tradingRow}>
          <Typography className={classes.tradingDex}>SushiSwap V3</Typography>
          <Typography className={classes.tradingPair}>E3D / WETH · 0.3% fee</Typography>
          <Typography style={{ fontSize: 13, color: '#9ca3af' }}></Typography>
          <Tooltip title="View on Sushi">
            <IconButton
              className={classes.iconBtn}
              size="small"
              component="a"
              href={SUSHI_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon style={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {metrics && (
          <Box style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Box>
              <Typography style={{ fontSize: 11, color: '#9ea4d8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, textAlign: 'left' }}>Pool Address</Typography>
              <Typography style={{ fontFamily: 'monospace', fontSize: 12, color: '#a5b4fc', marginTop: 3, textAlign: 'left' }}>{POOL_ADDRESS}</Typography>
            </Box>
            <Box>
              <Typography style={{ fontSize: 11, color: '#9ea4d8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, textAlign: 'left' }}>E3D in Pool</Typography>
              <Typography style={{ fontSize: 13, color: '#e5e7ff', fontWeight: 700, marginTop: 3, textAlign: 'left' }}>{fmt(metrics.e3dInPool, 0)} E3D</Typography>
            </Box>
            <Box>
              <Typography style={{ fontSize: 11, color: '#9ea4d8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, textAlign: 'left' }}>WETH in Pool</Typography>
              <Typography style={{ fontSize: 13, color: '#e5e7ff', fontWeight: 700, marginTop: 3, textAlign: 'left' }}>{fmt(metrics.wethInPool, 4)} WETH</Typography>
            </Box>
          </Box>
        )}
      </Paper>

      <Typography className={classes.note}>
        E3D is an ERC-20 token on Ethereum (18 decimals). Total supply is fixed at 1,000,000 E3D.
        Price and pool data are fetched live from on-chain sources.
        Allocation percentages and vesting schedules will be published as the protocol details are finalised.
      </Typography>
    </Box>
  );
}
